import React from "react";
import { Button } from "@mui/material";

const errors = {
  network: {
    code: "Connection Failed",
    title: "Unable to Connect",
    description:
      "We couldn't reach the server. This usually means the server is offline or your internet connection was interrupted.",
    icon: (
      <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#5b648f" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 1l22 22" />
        <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
        <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
        <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
        <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
        <line x1="12" y1="20" x2="12.01" y2="20" />
      </svg>
    ),
  },
  server: {
    code: "500",
    title: "Internal Server Error",
    description:
      "Something went wrong on our end. The server encountered an unexpected error while processing your request.",
    icon: (
      <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#5b648f" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <line x1="2" y1="10" x2="22" y2="10" />
        <line x1="6" y1="7" x2="6" y2="7.01" />
        <line x1="9" y1="7" x2="9" y2="7.01" />
        <path d="M10 16l2-2 2 2" />
        <line x1="12" y1="14" x2="12" y2="14.01" />
      </svg>
    ),
  },
};

const ServerError = ({ type = "network", onRetry, onDismiss }) => {
  const err = errors[type] || errors.network;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
      style={{
        backgroundColor: "rgba(244, 245, 249, 0.97)",
        backdropFilter: "blur(8px)",
      }}
    >
      <div className="flex flex-col items-center text-center max-w-md">
        {/* Icon container */}
        <div
          className="flex items-center justify-center mb-6 rounded-2xl"
          style={{
            width: 96,
            height: 96,
            backgroundColor: "rgba(91, 100, 143, 0.08)",
          }}
        >
          {err.icon}
        </div>

        {/* Error code badge */}
        <div
          className="inline-flex items-center px-3 py-1 rounded-full text-[12px] font-semibold tracking-wider uppercase mb-4"
          style={{
            backgroundColor: "rgba(91, 100, 143, 0.1)",
            color: "#5b648f",
          }}
        >
          {err.code}
        </div>

        {/* Title */}
        <h2 className="text-[24px] font-semibold text-gray-800 mb-2">
          {err.title}
        </h2>

        {/* Description */}
        <p className="text-[15px] text-gray-500 mb-8 leading-relaxed">
          {err.description}
        </p>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button
            variant="contained"
            onClick={onRetry}
            sx={{
              backgroundColor: "#5b648f",
              borderRadius: "12px",
              padding: "10px 32px",
              fontSize: "15px",
              fontWeight: 500,
              textTransform: "none",
              letterSpacing: "0.3px",
              boxShadow: "0 4px 14px rgba(91, 100, 143, 0.35)",
              "&:hover": {
                backgroundColor: "#4a5278",
                boxShadow: "0 6px 20px rgba(91, 100, 143, 0.45)",
              },
            }}
          >
            Try Again
          </Button>
          {onDismiss && (
            <Button
              variant="outlined"
              onClick={onDismiss}
              sx={{
                borderColor: "#d1d5db",
                borderRadius: "12px",
                padding: "10px 24px",
                fontSize: "15px",
                fontWeight: 500,
                textTransform: "none",
                color: "#6b7280",
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "rgba(0,0,0,0.02)",
                },
              }}
            >
              Dismiss
            </Button>
          )}
        </div>

        {/* Subtle hint */}
        <p className="text-[12px] text-gray-400 mt-6">
          If the problem persists, please contact your system administrator.
        </p>
      </div>
    </div>
  );
};

export default ServerError;
